import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import Account from '@/pages/Account/Account';
import ChangePasswordRedirect from '@/pages/ChangePasswordRedirect';
import Collaborators from '@/pages/Collaborators';
import Contact from '@/pages/Contact/Contact';
import CookiesPolicy from '@/pages/CookiesPolicy';
import Faqs from '@/pages/Faqs';
import Folder from '@/pages/Folder';
import Gdpr from '@/pages/Gdpr';
import Home from '@/pages/Home';
import MyDocuments from '@/pages/MyDocuments';
import NewDocument from '@/pages/NewDocument';
import NotFound from '@/pages/NotFound';
import Pricing from '@/pages/Pricing';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Terms from '@/pages/Terms';
import Unsubscribe from '@/pages/Unsubscribe';
import routes from '@/routes';
import { addLanguageParam } from '@/utils/locale';

import ErrorBoundary from './ErrorBoundary';
import {
	CheckoutLayout,
	EmptyLayout,
	LocalizedLayout,
	PrivateLayout,
	PublicLayout
} from './Layout';

async function lazyPdfEditor() {
	const { default: PdfEditor } = await import('@/pages/PdfEditor');
	return { Component: PdfEditor };
}

const router = createBrowserRouter([
	{
		path: '/',
		element: <PublicLayout />,
		errorElement: <ErrorBoundary />,
		children: [
			{
				path: '/',
				element: <LocalizedLayout />,
				children: [
					{
						// IMPORTANT: define the route id for setting the document title automatically
						// (translation key would be "pageTitles.{id}")
						id: 'home',
						path: addLanguageParam(routes.home),
						element: <Home />
					},
					{
						id: 'contact',
						path: addLanguageParam(routes.contact),
						element: <Contact />
					},
					{
						id: 'newDocument',
						path: routes.newDocument,
						element: <NewDocument />
					},
					{
						id: 'pricing',
						path: addLanguageParam(routes.pricing),
						element: <Pricing />
					},

					{
						id: 'faqs',
						path: addLanguageParam(routes.faqs),
						element: <Faqs />
					}
				]
			}
		]
	},
	{
		path: '/',
		element: <PublicLayout preventIndexing />,
		errorElement: <ErrorBoundary />,
		children: [
			{
				path: '/',
				element: <LocalizedLayout />,
				children: [
					{
						id: 'privacy',
						path: addLanguageParam(routes.privacyPolicy),
						element: <PrivacyPolicy />
					},
					{
						id: 'terms',
						path: addLanguageParam(routes.terms),
						element: <Terms />
					},
					{
						id: 'gdpr',
						path: addLanguageParam(routes.gdpr),
						element: <Gdpr />
					},
					{
						id: 'cookies',
						path: addLanguageParam(routes.cookiesPolicy),
						element: <CookiesPolicy />
					},
					{
						id: 'unsubscribe',
						path: addLanguageParam(routes.unsubscribe),
						element: <Unsubscribe />
					}
				]
			}
		]
	},
	{
		path: '/',
		element: <EmptyLayout preventIndexing />,
		errorElement: <ErrorBoundary />,
		children: [
			{
				id: 'editor',
				path: routes.editor,
				lazy: lazyPdfEditor,
				children: [
					{
						path: routes.fastCheckout,
						lazy: lazyPdfEditor
					}
				]
			},
			{
				path: routes.sharedDocument,
				async lazy() {
					const { default: Share } = await import('@/pages/Share');
					return { Component: Share };
				}
			}
		]
	},
	{
		path: '/',
		element: <PrivateLayout />,
		errorElement: <ErrorBoundary />,
		children: [
			{
				id: 'myAccount',
				path: routes.myAccount,
				element: <Account />
			},
			{
				id: 'myDocuments',
				path: routes.myDocuments,
				element: <MyDocuments />
			},
			{
				id: 'users',
				path: routes.users,
				element: <Collaborators />
			},
			{
				id: 'folder',
				path: routes.folder,
				element: <Folder />
			},
			{
				id: 'billing',
				path: routes.billing,
				async lazy() {
					const { default: Component } = await import('@/pages/Billing');
					return { Component };
				}
			}
		]
	},
	{
		path: '/',
		element: <CheckoutLayout />,
		errorElement: <ErrorBoundary />,
		children: [
			{
				id: 'checkout',
				path: routes.checkout,
				async lazy() {
					const { default: Component } = await import('@/pages/Checkout');
					return { Component };
				}
			}
		]
	},
	{
		path: routes.changePassword,
		element: <ChangePasswordRedirect />
	},
	{
		path: '*',
		element: <PublicLayout preventIndexing />,
		children: [{ id: 'notFound', path: '*', element: <NotFound /> }]
	}
]);

function Router() {
	return <RouterProvider router={router} />;
}

export default Router;
